import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createAdminGuild, createGuildUploadCode, fetchAdminGuilds } from '../api/guilds';
import { fetchServers } from '../api/servers';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';

export function AdminGuildsPage() {
  const queryClient = useQueryClient();
  const guildsQuery = useQuery({ queryKey: ['admin-guilds'], queryFn: fetchAdminGuilds });
  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: fetchServers });
  const [name, setName] = useState('');
  const [serverId, setServerId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [codeLabels, setCodeLabels] = useState<Record<string, string>>({});
  const [generatedCodes, setGeneratedCodes] = useState<Record<string, string>>({});

  const firstServerId = useMemo(() => serversQuery.data?.[0]?.id ?? '', [serversQuery.data]);

  const createGuildMutation = useMutation({
    mutationFn: () => createAdminGuild({ serverId: Number(serverId || firstServerId), name, description }),
    onSuccess: async () => {
      setName('');
      setDescription('');
      await queryClient.invalidateQueries({ queryKey: ['admin-guilds'] });
      await queryClient.invalidateQueries({ queryKey: ['guilds'] });
    }
  });

  const createCodeMutation = useMutation({
    mutationFn: ({ guildId, label }: { guildId: string; label?: string }) => createGuildUploadCode(guildId, { label }),
    onSuccess: async (_code, variables) => {
      setCodeLabels((current) => ({ ...current, [variables.guildId]: '' }));
      if (_code.code) setGeneratedCodes((current) => ({ ...current, [variables.guildId]: _code.code! }));
      await queryClient.invalidateQueries({ queryKey: ['admin-guilds'] });
    }
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || !Number(serverId || firstServerId)) return;
    createGuildMutation.mutate();
  }

  return (
    <div className="container-fluid px-3 px-lg-4 py-4">
      <div className="mb-4">
        <h1 className="h2 mb-1">Guild Management</h1>
        <p className="text-body-secondary mb-0">Create official guild records and rotate upload codes used by fzClient.</p>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header"><h2 className="h5 mb-0">Create Guild</h2></div>
        <div className="card-body">
          <form className="row g-3" onSubmit={handleSubmit}>
            <div className="col-12 col-md-4">
              <label className="form-label">Server</label>
              <select className="form-select" value={serverId} onChange={(event) => setServerId(Number(event.target.value))}>
                <option value="">Choose server</option>
                {(serversQuery.data ?? []).map((server) => <option key={server.id} value={server.id}>{server.name}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Guild Name</label>
              <input className="form-control" value={name} onChange={(event) => setName(event.target.value)} placeholder="Example Guild" />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Description</label>
              <input className="form-control" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Optional" />
            </div>
            <div className="col-12">
              <button className="btn btn-primary" disabled={createGuildMutation.isPending || !name.trim()} type="submit">Create Guild</button>
            </div>
          </form>
        </div>
      </div>

      {guildsQuery.isLoading ? <LoadingState label="Loading guilds" /> : null}
      {guildsQuery.isError ? <ErrorState title="Could not load guilds" error={guildsQuery.error} /> : null}

      {guildsQuery.data ? (
        <div className="card shadow-sm">
          <div className="table-responsive">
            <table className="table table-dark align-middle mb-0">
              <thead><tr><th>Guild</th><th>Server</th><th>Counts</th><th>New Upload Code</th></tr></thead>
              <tbody>
                {guildsQuery.data.map((guild) => (
                  <tr key={guild.id}>
                    <td><div className="fw-semibold">{guild.name}</div><div className="small text-body-secondary">{guild.description || guild.slug}</div></td>
                    <td>{guild.server?.name ?? 'Unknown'}</td>
                    <td className="small text-body-secondary">{guild.counts.encounters} encounters · {guild.counts.uploadCodes} codes</td>
                    <td style={{ minWidth: 360 }}>
                      <div className="input-group input-group-sm mb-2">
                        <input className="form-control" placeholder="Code label, e.g. Officer code" value={codeLabels[guild.id] ?? ''} onChange={(event) => setCodeLabels((current) => ({ ...current, [guild.id]: event.target.value }))} />
                        <button className="btn btn-outline-light" type="button" onClick={() => createCodeMutation.mutate({ guildId: guild.id, label: codeLabels[guild.id] })}>Generate</button>
                      </div>
                      {generatedCodes[guild.id] ? <div className="alert alert-success py-2 px-2 mb-0 small"><div className="fw-semibold">New code</div><code>{generatedCodes[guild.id]}</code></div> : <small className="text-body-secondary">Generated codes are shown once here. Rotate/revoke from backend/admin as needed.</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
